import React, { useEffect, useState } from "react";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import ParticleNet from "./components/ParticleNet";
import { projects, skills } from "./data";
import "./App.css";

function initials(title) {
  return title
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

export default function App() {
  // Parallax offset for the hero, driven by scroll position.
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="app">
      {/* Fixed interactive background across the whole page */}
      <ParticleNet />
      <div className="bg-gradient" aria-hidden="true" />

      {/* NAV */}
      <nav className="nav">
        <span className="nav-logo">GB</span>
        <div className="nav-links">
          <a href="#about">About</a>
          <a href="#projects">Projects</a>
          <a href="#contact">Contact</a>
        </div>
      </nav>

      {/* HERO */}
      <header className="hero" id="top">
        <div
          className="hero-content"
          style={{
            transform: `translateY(${scrollY * 0.3}px)`,
            opacity: Math.max(1 - scrollY / 600, 0),
          }}
        >
          <p className="hero-kicker">Hi, I'm</p>
          <h1 className="name">Grishan Bajracharya</h1>
          <p className="tagline">
            Software engineer turning caffeine and curiosity into clean,
            fast, delightful web apps.
          </p>
          <div className="hero-cta">
            <a className="btn btn-primary" href="#projects">
              View my work
            </a>
            <a className="btn btn-ghost" href="#contact">
              Get in touch
            </a>
          </div>
          <div className="links">
            <a
              href="https://www.linkedin.com/in/grishan-bajracharya-b77a7b218/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <FaLinkedin />
            </a>
            <a
              href="https://github.com/tygbspartan"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <FaGithub />
            </a>
          </div>
        </div>
        <div className="scroll-hint" aria-hidden="true">
          <span />
        </div>
      </header>

      {/* ABOUT / SKILLS */}
      <section className="section about" id="about">
        <div className="section-inner">
          <h2 className="section-title">
            <span className="section-num">01.</span> About me
          </h2>
          <p className="about-text">
            I'm a full-stack software engineer who loves building products
            end to end — from crafting responsive, animated interfaces to
            designing the APIs and data models behind them. I care about
            clean code, thoughtful UX, and shipping things that feel fast.
          </p>
          <h3 className="skills-heading">Things I work with</h3>
          <ul className="skills">
            {skills.map((skill) => (
              <li key={skill} className="skill-chip">
                {skill}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="section projects" id="projects">
        <div className="section-inner">
          <h2 className="section-title">
            <span className="section-num">02.</span> Projects
          </h2>
          <p className="section-sub">
            A few things I've designed, built, or been part of.
          </p>
          <div className="project-grid">
            {projects.map((project) => (
              <article className="project-card" key={project.title}>
                <div
                  className="project-thumb"
                  style={{
                    background: project.image
                      ? undefined
                      : `linear-gradient(135deg, ${project.accent}33, #0b0f1a 70%)`,
                  }}
                >
                  {project.image ? (
                    <img
                      src={project.image}
                      alt={project.title + " screenshot"}
                      loading="lazy"
                    />
                  ) : (
                    <span
                      className="project-thumb-initials"
                      style={{ color: project.accent }}
                    >
                      {initials(project.title)}
                    </span>
                  )}
                </div>
                <div className="project-body">
                  <h3 className="project-title">{project.title}</h3>
                  <p className="project-desc">{project.description}</p>
                  <ul className="project-tech">
                    {project.tech.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                  <a
                    className="project-link"
                    href={project.link}
                    target={project.link === "#" ? undefined : "_blank"}
                    rel="noopener noreferrer"
                  >
                    View project →
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section className="section contact" id="contact">
        <div className="section-inner contact-inner">
          <h2 className="section-title centered">
            <span className="section-num">03.</span> Get in touch
          </h2>
          <p className="contact-text">
            Got a project, a role, or just want to say hi? My inbox is
            always open.
          </p>
          <a
            className="btn btn-primary"
            href="mailto:grishanprofessional@gmail.com"
          >
            Say hello
          </a>
          <div className="links contact-links">
            <a
              href="https://www.linkedin.com/in/grishan-bajracharya-b77a7b218/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <FaLinkedin />
            </a>
            <a
              href="https://github.com/tygbspartan"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <FaGithub />
            </a>
          </div>
        </div>
      </section>

      <footer className="footer">
        <p>Built with React &amp; a lot of ☕ — Grishan Bajracharya</p>
      </footer>
    </div>
  );
}
